import { inject, reactive, ref, computed } from 'vue';

import PaginationComponent from '@/components/_shared/_partials/pagination/pagination.vue'
import FilterSelectComponent from '@/components/_shared/_partials/filter/filter.vue'
import Card from '@/components/content/views/card/card.vue';
import FormattingService from '@/services/formatting.service';
import { Api } from '@/services/api.service'
import Tooltip from '@/components/content/views/tooltip/tooltip.vue';
import SkeletonLoader from '@/components/content/views/skeleton-loader/skeleton-loader.vue';
import Toolkit from '@/components/content/types/toolkit/toolkit.vue';
import Discount from '@/components/content/types/discount/discount.vue';
import Tutorial from '@/components/content/types/tutorial/tutorial.vue';
import Track from '@/components/content/types/track/track.vue';
import Webcast from '@/components/content/types/webcast/webcast.vue';
import Article from '@/components/content/types/article/article.vue';
import Course from '@/components/content/types/course/course.vue';
import Magazine from '@/components/content/types/magazine/magazine.vue';
import Lesson from '@/components/content/types/lesson/lesson.vue';
import EventSession from '@/components/content/types/event-session/event-session.vue';
import Instructor from '@/components/content/types/instructor/instructor.vue';
import Modal from '@/components/content/views/modal/modal.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDown,
  faAngleUp,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faAngleDown)
library.add(faAngleUp)

export default {
  name: 'DefaultCollection',
  props: {
    user: {
      type: Object,
      default: {id: null, token: null}
    },
    collectionType: {
      type: String,
      required: false,
    },
    data: {
      type: Array,
      default: [],
    },
    taxonomyTermId: {
      type: Number,
      required: false,
    },
    filters: {
      type: Array,
      required: false,
    },
    page: {
      type: Number,
      required: false,
    },
    mixedContent: {
      type: Boolean,
      default: false
    },
    instructorId: {
      type: Number,
      required: false,
    },
    reload: {
      type: Boolean,
      default: false
    },
    section: {
      type: Object,
      required: false,
    },
    perPage: {
      type: Number,
      default: 12
    },
    altBackground: {
      type: Boolean,
      default: false
    },
    contentIds: {
      type: Array,
      required: false,
    }
  },
  components: {
    Card,
    Tooltip,
    SkeletonLoader,
    PaginationComponent,
    FilterSelectComponent,
    Toolkit,
    Discount,
    Tutorial,
    Track,
    Webcast,
    Article,
    Course,
    Magazine,
    Modal,
    Lesson,
    EventSession,
    FontAwesomeIcon,
    Instructor,
  },
  setup (props, {emit} ) {
    const config = inject('config');
    const authProvider = inject('authProvider');
    const memberProvider = inject('memberProvider');
    const collectionsProvider = inject('collectionsProvider');
    const sectionsProvider = inject('sectionsProvider');

    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');

    let jwpScript = document.createElement('script');
    jwpScript.setAttribute('src',
      'https://cdn.jwplayer.com/libraries/XLfxzvnU.js');
    document.head.appendChild(jwpScript);

    const defaultCollection = ref(null);
    const state = reactive({
      member: computed(() => collectionsProvider().state.member ? collectionsProvider().state.member : null),
      loaded: false,
      passedInData: computed(() => props.data),
      apiData: [],
      data: computed(() => props.section ? state.passedInData : state.apiData),
      pagination: null,
      filters: props.filters && props.filters.length ? props.filters[0].data : null,
      url: null,
      getPagination: computed(() => state.pagination),
      hasPagination: computed(() => Boolean(state.pagination)),
      skeletonCount: computed(() => state.passedInData.length ? state.passedInData.length : parseInt(props.perPage)),
      type: computed(() => props.collectionType),
      modalItem: {},
      showModal: false,
      magazineTab: 'all',
      filteredId: computed(() => state.selectedFilter ? state.selectedFilter.id : props.taxonomyTermId),
      openOptions: false,
      xcoord: 0,
      ycoord: 0,
      width: 0,
      outOfBounds: false,
      select: 'All '+props.collectionType,
      page: props.page,
      selectedFilter: null,
      filterType: computed(() => {
        if(props.collectionType === 'webcasts') {
          return 'show'
        }
        if(props.collectionType === 'magazines') {
          return 'publication';
        }
        if(props.collectionType === 'tutorials') {
          return 'tutorialtype';
        }
        return null;
      }),
      label: computed(() => {
        if(state.select === 'The Grid') {
          return 'The Grid - Watch Live Wednesdays at 1PM ET'
        }
        return state.select;
      })
    });

    const loadData = async () => {
      let response = await Api.get(state.url, headers);
      if (response.status !== 'error') {
        if(response.data.meta) {
          if(response.data.meta.outOfBounds) {
            state.outOfBounds = true;
            return;
          }
          if(response.data.meta.pagination) {
            state.pagination = response.data.meta.pagination;
          } else {
            state.pagination = response.data.meta;
          }
          if(response.data.meta.filters && response.data.meta.filters.length) {
            state.filters = response.data.meta.filters[0].data;
          }
        }
        if(response.data.data.results) {
          state.apiData = response.data.data.results;
        } else {
          state.apiData = response.data.data;
        }
        state.outOfBounds = false;
        if (config.debug) {
          console.log('Default Collection loadData(): response data', response)
        }
      }
    }

    const setFilter = async () => {
      let filter = state.filters.find(filter => {
        return filter.id == props.taxonomyTermId;
      })
      if(filter) {
        state.selectedFilter = filter;
        state.select = filter.label;
        buildUrl();
      }
    }

    const initialize = async () => {
      state.loaded = false;
      if(props.data.length && !props.collectionType) {
        if(props.user.id && props.user.token) {
          await collectionsProvider().loadMember(props.user, config, authProvider);
        }
        state.loaded = true;
        return;
      }
      const decoded = decodeURIComponent(window.location.href);
      const query = decoded.replace('?', '');
      const firstRegex = 'page=\\d{1,}';
      const existsInParam = query.match(firstRegex);
      const secondRegex = 'page/\\d{1,}';
      const existsInStructure = query.match(secondRegex);
      let page = 0;
      if(existsInParam) {
        let temp = existsInParam[0].split('=');
        page = temp[1];
      }
      if(existsInStructure) {
        let temp = existsInStructure[0].split('/');
        page = temp[1];
      }
      if(props.taxonomyTermId && props.filters) {
        await setFilter();
      }
      buildUrl();
      if(props.user) {
        await collectionsProvider().loadMember(props.user, config, authProvider);
      }
      if(!props.data.length) {
        await loadData();
      }
      state.loaded = true;
    };

   const paginate = async (page) => {
      state.loaded = false;
      state.page = page;
      buildUrl();
      await loadData();
      window.scrollTo(state.xcoord, state.ycoord);
      state.loaded = true;
    }

    const toggleModal = async (item) => {
      if(item) {
        state.modalItem = item;
      } else {
        if(state.modalItem.trailer) {
          let player = jwplayer('jwplayer');
          player.remove();
        }
        state.modalItem = {};
      }
      state.showModal = !state.showModal;
    }

    const filterResults = async (filterId) => {
      state.page = 1;
      if(filterId == 'all') {
        state.select = `all ${props.collectionType}`
        state.loaded = false;
        state.selectedFilter = null;
        buildUrl()
        await loadData();
        state.loaded = true;
      } else {
        state.loaded = false;
        let matchedFilter = state.filters.find( filter => {
          return filter.id === filterId;
        });
        if(matchedFilter) {
          state.selectedFilter = matchedFilter;
          state.select = matchedFilter.label;
          buildUrl();
          await loadData();
          state.loaded = true;
        }
      }
    }

    const buildUrl = () => {
      let url = `${config.api.uri}/${config.api.version}`;
      if(props.contentIds && props.contentIds.length) {
        let type = props.collectionType ? props.collectionType : 'courses';
        let ids = props.contentIds.join(",");
        url = `${url}/${type}?filter[content_ids]=${ids}&page=${state.page}&per_page=${props.perPage}&include=instructors,lessons,categories,courses,excerpt,publication&orderby=featured`;
      }
      else {
        if(props.mixedContent && props.instructorId) {
          url = `${url}/instructors/${props.instructorId}/content?merge_results&token=${props.user.token}`;
        } else {
          url = `${url}/${props.collectionType}?user=${props.user.id}&token=${props.user.token}&per_page=${props.perPage}&include=instructors,lessons,categories,courses,excerpt,publication&orderby=featured`;
        }
        if(state.page) {
          url = `${url}&page=${state.page}`;
        }
        if(state.selectedFilter) {
          url = `${url}&filter[${state.filterType}]=${state.selectedFilter.id}`;
        }
      }
      state.url = url;
    }

    const reloadSection = async (bookmarkOrArchive) => {
      if(props.reload && props.section && props.user) {
        await sectionsProvider().reloadSection(props.user, bookmarkOrArchive);
      }
    }

    initialize();

    return {
      FormattingService,
      state,
      paginate,
      toggleModal,
      defaultCollection,
      filterResults,
      reloadSection,
    };
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
    this.state.xcoord = this.defaultCollection.getBoundingClientRect().left;
    this.state.ycoord = this.defaultCollection.getBoundingClientRect().top - 100;
    this.state.width = this.defaultCollection.getBoundingClientRect().right;
  },
  methods: {
    onResize() {
      this.state.xcoord = this.defaultCollection.getBoundingClientRect().left;
      this.state.ycoord = this.defaultCollection.getBoundingClientRect().top - 100;
      this.state.width = this.defaultCollection.getBoundingClientRect().right;
    },
  }
};
